/**
 * Alternative littera name (`assignment.alternativeName`).
 *
 * The alternative name is a per-project cost-tracking label: it replaces the
 * littera name everywhere inside Kustannusseuranta (holders, budget grid, pie
 * chart, invoice rows, littera dropdowns) but nowhere else. Cost reports,
 * media-tag matching and every view outside the scene keep `assignment.name`.
 *
 * Storage rules: the attribute lives on the assignment item as a plain string.
 * An empty / whitespace-only value means "no alternative name" and is sent as
 * `null` to the REST update endpoint, which REMOVEs the attribute. On create
 * the key is omitted instead, so a NULL attribute is never written.
 */

/**
 * @param {object} [assignment]
 * @returns {string} trimmed alternative name, '' when not set
 */
export const getAlternativeName = (assignment) =>
  typeof assignment?.alternativeName === 'string'
    ? assignment.alternativeName.trim()
    : '';

/**
 * Name shown inside Kustannusseuranta: the alternative name when set,
 * otherwise the original name.
 * @param {object} [assignment]
 * @returns {string}
 */
export const getAssignmentDisplayName = (assignment) =>
  getAlternativeName(assignment) || assignment?.name || '';

/**
 * True when an alternative name is set and differs from the original name,
 * i.e. when the original name is worth showing as secondary text.
 * @param {object} [assignment]
 * @returns {boolean}
 */
export const hasDistinctAlternativeName = (assignment) => {
  const alternativeName = getAlternativeName(assignment);
  return alternativeName !== '' && alternativeName !== (assignment?.name || '');
};

/**
 * Text a littera search matches against: number, original name and the
 * alternative name.
 * @param {object} [assignment]
 * @returns {string}
 */
export const getAssignmentSearchText = (assignment) =>
  [assignment?.number, assignment?.name, getAlternativeName(assignment)]
    .filter(Boolean)
    .join(' ');

/**
 * Value to persist: trimmed string, or `null` (= REMOVE the attribute) for an
 * empty / whitespace-only / non-string value.
 * @param {*} value
 * @returns {string|null}
 */
export const normalizeAlternativeName = (value) => {
  const trimmed = typeof value === 'string' ? value.trim() : '';
  return trimmed === '' ? null : trimmed;
};

/**
 * Returns a copy of the assignment with `alternativeName` normalised for the
 * REST create-and-update endpoint. On update a cleared name is sent as `null`
 * so the backend REMOVEs it; on create (`forCreate`) the key is omitted
 * instead, because a NULL attribute must never be PUT.
 * @param {object} assignment
 * @param {{forCreate?: boolean}} [options]
 * @returns {object}
 */
export const withNormalizedAlternativeName = (
  assignment,
  { forCreate = false } = {}
) => {
  const alternativeName = normalizeAlternativeName(
    assignment?.alternativeName
  );
  if (alternativeName === null && forCreate) {
    const { alternativeName: _omit, ...rest } = assignment || {};
    return rest;
  }
  return { ...assignment, alternativeName };
};
